/**
 * A PICKUP the crew member is on (workstream C) for the Today / My Week lists —
 * the pickup counterpart of CrewProjectCard, same visual grammar. Tapping opens
 * the crew pickup screen (Start/Finish + the packing lists to check off).
 */
import { format, parseISO } from 'date-fns';
import { ChevronRight, PackageCheck } from 'lucide-react';
import type { CrewTodayPickup } from '@vari-crew/schemas';
import { CrewAvatars } from '@/components/ui/CrewAvatars';
import { cn } from '@/lib/cn';

function timeOnly(iso: string | null): string {
  if (!iso) return '';
  return format(parseISO(iso), 'h:mma').toLowerCase();
}

const ACTIVE = new Set(['in_progress']);

export function CrewPickupCard({
  pickup,
  expanded,
  onOpen,
}: {
  pickup: CrewTodayPickup;
  /** Full card (Today) vs. compact row (My Week). */
  expanded: boolean;
  onOpen: () => void;
}) {
  const isActive = ACTIVE.has(pickup.status);
  const isDone = pickup.status === 'complete';
  const visualClass = isDone
    ? 'bg-status-complete text-status-complete-fg border-status-complete-fg/20'
    : isActive
      ? 'bg-status-onsite text-status-onsite-fg border-status-onsite-fg/40 ring-1 ring-status-onsite-fg/30'
      : 'bg-bg border-line';

  const meta = `Pickup · ${pickup.project_count} ${pickup.project_count === 1 ? 'order' : 'orders'}${
    pickup.is_lead ? ' · you lead' : ''
  }`;

  if (!expanded) {
    return (
      <button
        onClick={onOpen}
        className={cn('flex items-center gap-3 p-3 rounded-xl border text-sm w-full text-left', visualClass)}
      >
        <PackageCheck className="w-4 h-4 flex-shrink-0 opacity-70" />
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate">
            {isDone && '✓ '}
            {isActive && '▶ '}
            {timeOnly(pickup.scheduled_start)} · {pickup.title}
          </div>
          <div className="text-xs opacity-75 truncate">{meta}</div>
        </div>
        {pickup.crew.length > 0 && <CrewAvatars crew={pickup.crew} max={3} className="shrink-0" />}
        <ChevronRight className="w-4 h-4 opacity-60 flex-shrink-0" />
      </button>
    );
  }

  return (
    <button onClick={onOpen} className={cn('p-4 rounded-2xl border w-full text-left', visualClass)}>
      <div className="flex items-start gap-2.5">
        <PackageCheck className="w-5 h-5 mt-0.5 flex-shrink-0 opacity-80" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-base leading-tight">
            {isActive && '▶ '}
            {timeOnly(pickup.scheduled_start)} · {pickup.title}
          </div>
          <div className="text-xs opacity-80 mt-0.5">{meta}</div>
        </div>
        <ChevronRight className="w-4 h-4 mt-1 opacity-50 flex-shrink-0" />
      </div>
      {pickup.crew.length > 0 && (
        <CrewAvatars crew={pickup.crew} withNames className="mt-2" nameClass="opacity-80" />
      )}
    </button>
  );
}
